import { useMemo, useCallback } from "react";

import { useQuery } from "@tanstack/react-query";

import { useTranslate } from "@similarweb/i18n-provider";

import {
    OptionalTagWrapper,
    SectionTitle,
    TagsDropdownContainer,
} from "app/components/dashboard/widget-wizard/components/campaigns/CampaignsDashboardWizardStyled";
import { apiClient } from "app/modules/api-client/apiClient";
import { mapCurrentCampaign } from "app/pages/digital-marketing/keywords-campaigns/infra/campaignMappers";
import { IncludeExcludeTagsFilter } from "app/pages/digital-marketing/keywords-campaigns/reports/page-filters/tags/IncludeExcludeTagsFilter";
import type { IncludeExcludeTagsState } from "app/pages/digital-marketing/keywords-campaigns/reports/page-filters/tags/types";
import {
    parseTagsParam,
    resolveUntaggedFilter,
} from "app/pages/digital-marketing/keywords-campaigns/reports/page-filters/tags/utils";
import type { UntaggedFilterValue } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { TagsDropdownTrigger } from "app/pages/digital-marketing/keywords-campaigns/dashboard-widget-wizard/TagsDropdown/TagsDropdownTrigger";

const formatFilters = (filters: Record<string, unknown>) => {
    return Object.entries(filters).map(([name, value]) => ({ name, value }));
};

type TagsDropdownProps = {
    campaignId: string | undefined;
    widgetFilters: Record<string, unknown>;
    onApprove: (filters: { name: string; value: unknown }[]) => void;
    onDropDownToggle?: (isOpen: boolean) => void;
};

export const TagsDropdown = ({ campaignId, widgetFilters, onApprove, onDropDownToggle }: TagsDropdownProps) => {
    const { translate } = useTranslate();

    const { data: campaignWithTags } = useQuery({
        enabled: !!campaignId,
        queryKey: ["dashboard-wizard-campaign", campaignId],
        queryFn: async () => {
            const campaign = await apiClient.RankTracker.rtGetCampaign(campaignId!);
            return mapCurrentCampaign(campaign, { isShared: false });
        },
        refetchOnMount: true,
        staleTime: 0,
    });

    const initialState: IncludeExcludeTagsState = useMemo(
        () => ({
            includeTags: parseTagsParam(widgetFilters.tags as string | undefined),
            excludeTags: parseTagsParam(widgetFilters.excludeTags as string | undefined),
            untaggedFilter: resolveUntaggedFilter(
                widgetFilters.untaggedFilter as UntaggedFilterValue | undefined,
                widgetFilters.withUntagged?.toString(),
            ),
        }),
        [widgetFilters.tags, widgetFilters.excludeTags, widgetFilters.untaggedFilter, widgetFilters.withUntagged],
    );

    const handleApply = useCallback(
        (newState: IncludeExcludeTagsState) => {
            const updatedFilters = {
                ...widgetFilters,
                tags: newState.includeTags.join(",") || undefined,
                excludeTags: newState.excludeTags.join(",") || undefined,
                untaggedFilter: newState.untaggedFilter,
                withUntagged: undefined, // clear legacy param
            };

            onApprove(formatFilters(updatedFilters));
        },
        [widgetFilters, onApprove],
    );

    const handleClear = useCallback(() => {
        const updatedFilters = {
            ...widgetFilters,
            tags: undefined,
            excludeTags: undefined,
            untaggedFilter: undefined,
            withUntagged: undefined,
        };

        onApprove(formatFilters(updatedFilters));
    }, [widgetFilters, onApprove]);

    return (
        <>
            <FlexRow>
                <SectionTitle>{translate("home.dashboards.wizard.tags.title")}</SectionTitle>
                <OptionalTagWrapper>{translate("home.dashboards.wizard.competitors.optional")}</OptionalTagWrapper>
            </FlexRow>
            <TagsDropdownContainer>
                <IncludeExcludeTagsFilter
                    popoverOffset={-40}
                    campaignConfiguration={campaignWithTags}
                    appliedState={initialState}
                    onApply={handleApply}
                    onClear={handleClear}
                    onOpenChange={onDropDownToggle}
                    renderTrigger={TagsDropdownTrigger}
                />
            </TagsDropdownContainer>
        </>
    );
};
